import {
    BackgroundSection,
    ElementsSection,
    LayersSection,
    PhotosSection,
    Section,
    SectionTab,
    SidePanel,
    SizeSection,
    TextSection,
} from 'polotno/side-panel';
import React, { useEffect, useState } from 'react';
import { PolotnoShuffleButton } from './actionButtons/PolotnoShuffleButton';
import { PolotnoQuickSaveButton } from './actionButtons/PolotnoQuickSaveButton';
import CustomUpload from './tabs/CustomUpload';
import Generate from './tabs/Generate';
import Caption from './tabs/Caption';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import {
    getAdFromChat,
    getAdFromCreateTab,
    getTemplateFromProject,
    getTemplateLoadingState,
} from '../../store/selector/editor.selector';
import { getDesignElements } from '../../store/selector/brand-elements.selector';
import { useCreateOrUpdateProjectMutation } from '../../store/api/editor.api';
import {
    unstable_setAnimationsEnabled,
    unstable_setTextOverflow,
    unstable_setTextVerticalResizeEnabled,
} from 'polotno/config';
import {
    SetEditorTemplateData,
    SetProjectCreationStatus,
    SetTemplateLoadingState,
} from '../../store/slice/editor.slice';
import { addCorrectFontsInTemplate } from '../../utils/template.util';
import { ICreateOrUpdateProjectResponse } from '../../interfaces/project.interface';
import { ITemplateFromProject } from '../../interfaces/common.interface';
import { displayErrorToast } from '../../utils/ToastUtils';
import { useNavigate } from 'react-router-dom';
import { PolotnoContainer, SidePanelWrap, WorkspaceWrap } from 'polotno';
import { Button, Spinner } from 'react-bootstrap';
import Toolbar from 'polotno/toolbar/toolbar';
import Workspace from 'polotno/canvas/workspace';
import ZoomButtons from 'polotno/toolbar/zoom-buttons';
import Variants from './tabs/Variants';

const ActionControls = ({ store }: { store: any }) => {
    return (
        <div className="d-flex justify-content-between align-item-center me-3">
            <PolotnoShuffleButton store={store} />
            <PolotnoQuickSaveButton store={store} />
            {/* <PolotnoDownloadButton store={store} /> */}
        </div>
    );
};

const CustomUploadSection: Section = {
    name: 'Assets',
    Tab: Object.assign(
        (props: any) => (
            <SectionTab name="Assets" {...props}>
                <i className="ph ph-file-image lg"></i>
            </SectionTab>
        ),
        { displayName: 'Assets' },
    ),
    Panel: ({ store }) => {
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CustomUpload store={store} />
            </div>
        );
    },
};

const VariantsSection: Section = {
    name: 'Variants',
    Tab: Object.assign(
        (props: any) => (
            <SectionTab name="Variants" {...props}>
                <i className="ph ph-folder-simple-plus lg"></i>
            </SectionTab>
        ),
        { displayName: 'Variants' },
    ),
    Panel: ({ store }) => {
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Variants store={store} />
            </div>
        );
    },
};

const GenerateSection: Section = {
    name: 'Generate',
    Tab: Object.assign(
        (props: any) => (
            <SectionTab name="Generate" {...props}>
                <i className="ph-fill ph-sparkle lg"></i>
            </SectionTab>
        ),
        { displayName: 'Generate' },
    ),
    Panel: ({ store }) => {
        return <Generate store={store} />;
    },
};

const CaptionSection: Section = {
    name: 'Caption',
    Tab: Object.assign(
        (props: any) => (
            <SectionTab name="Caption" {...props}>
                <i className="ph ph-closed-captioning lg"></i>
            </SectionTab>
        ),
        { displayName: 'Caption' },
    ),
    Panel: ({ store }) => {
        return <Caption store={store} />;
    },
};

interface IProps {
    store: any;
}

const PolotnoEditor = ({ store }: IProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const activeBrand = useAppSelector(getActiveBrand);
    const templateLoadingState = useAppSelector(getTemplateLoadingState);
    const adFromChat = useAppSelector(getAdFromChat);
    const adFromCreateTab = useAppSelector(getAdFromCreateTab);
    const templateFromProject = useAppSelector(getTemplateFromProject);
    const designElementsData = useAppSelector(getDesignElements);
    const [createOrUpdateProject] = useCreateOrUpdateProjectMutation();

    unstable_setTextVerticalResizeEnabled(true);
    unstable_setTextOverflow('change-font-size');
    unstable_setAnimationsEnabled(true);

    const clearStore = async () => {
        store.clear();
        store.addPage();
        await store.waitLoading();
    };

    const handleNewProject = async () => {
        if (!store || !activeBrand) return;
        const projectName = window.prompt('Enter Project name:');
        if (!projectName) return;

        dispatch(SetTemplateLoadingState(true));
        dispatch(SetProjectCreationStatus(true));
        await clearStore();
        dispatch(
            SetEditorTemplateData({
                adFromChat: null,
                adFromCreateTab: null,
                templateFromProject: null,
            }),
        );

        try {
            const projectJSON = store.toJSON();
            const res: any = await createOrUpdateProject({
                name: projectName,
                json: addCorrectFontsInTemplate(projectJSON, designElementsData),
                brandId: activeBrand.id,
            });
            const projectData = res.data as ICreateOrUpdateProjectResponse;
            const templateFromProject: ITemplateFromProject = {
                id: projectData.id,
                name: projectData.name,
                templateJSON: projectJSON,
            };
            dispatch(
                SetEditorTemplateData({
                    adFromChat: null,
                    adFromCreateTab: null,
                    templateFromProject,
                }),
            );
            dispatch(SetTemplateLoadingState(false));
            dispatch(SetProjectCreationStatus(false));
        } catch (error) {
            displayErrorToast('Error creating project, please refresh the page.');
        }
    };

    const handleOpenProject = async () => {
        navigate('/projects');
    };

    const getEditorSections = () => {
        if (!adFromChat && !adFromCreateTab && !templateFromProject)
            return [GenerateSection, CustomUploadSection];
        if (adFromCreateTab) {
            return [
                CustomUploadSection,
                CaptionSection,
                VariantsSection,
                SizeSection,
                BackgroundSection,
                LayersSection,
                TextSection,
                PhotosSection,
                ElementsSection,
            ];
        }
        return [
            GenerateSection,
            CustomUploadSection,
            VariantsSection,
            SizeSection,
            BackgroundSection,
            LayersSection,
            TextSection,
            PhotosSection,
            ElementsSection,
        ];
    };

    return (
        <div className="tw-w-full tw-h-full tw-border tw-border-blue-50 tw-rounded-md tw-p-1 tw-shadow-[4px_rgba(0,0,0,0.25)]">
            <PolotnoContainer>
                <div>
                    <SidePanelWrap>
                        <SidePanel
                            store={store}
                            sections={getEditorSections()}
                            defaultSection="None"
                        />
                    </SidePanelWrap>
                </div>

                {templateLoadingState ? (
                    <div
                        className="projectLoader"
                        style={{ padding: '24px', background: '#f9f9f9' }}
                    >
                        <Spinner
                            as="span"
                            className="mr-1 pe-none"
                            role="status"
                            aria-hidden="true"
                            animation="border"
                            style={{
                                marginTop: '40px',
                                width: '48px',
                                height: '48px',
                                color: '#967AA7',
                                background: 'transparent',
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {!adFromChat && !adFromCreateTab && !templateFromProject ? (
                            <div className="empty-editor-container d-flex flex-column align-items-center justify-content-center gap-3">
                                <div className="text-center">
                                    Please create a new project or open an existing project
                                </div>
                                <Button variant="primary" onClick={handleNewProject} size="lg">
                                    <>
                                        <i className="ph ph-plus sm"></i> New Project
                                    </>
                                </Button>
                                <Button variant="primary" onClick={handleOpenProject} size="lg">
                                    <>Open Project</>
                                </Button>
                            </div>
                        ) : (
                            <div style={{ width: '100%' }}>
                                <WorkspaceWrap>
                                    <Toolbar
                                        store={store}
                                        components={{
                                            ActionControls,
                                        }}
                                    />
                                    <Workspace
                                        store={store}
                                        // components={{ PageControls: () => null }}
                                        backgroundColor="white"
                                    />
                                    <ZoomButtons store={store} />
                                </WorkspaceWrap>
                            </div>
                        )}
                    </>
                )}
            </PolotnoContainer>
        </div>
    );
};

export default PolotnoEditor;
